/**
 * HamlaSDK v6 - Thin Client for Server-Decided Orchestration
 *
 * The server is the brain. The SDK is the hands.
 *
 * Architecture:
 * - Loader phase: init, triggers, tracking, events, MicroAdapter, LiveSlots, ChunkLoader
 * - Render chunk: popups, toasts, banners — loaded async via ChunkLoader
 *
 * Flow: Init → Detect adapter → Fetch instructions → Arm triggers → Render via chunk → Report events
 */

import { Logger } from './core/Logger';
import { CampaignFetcher } from './api/CampaignFetcher';
import { InstructionExecutor } from './orchestration/InstructionExecutor';
import { CloseMemory } from './orchestration/CloseMemory';
import { PopupReferee } from './orchestration/PopupReferee';
import { createWebPushOptInController, type WebPushOptInController } from './web-push-opt-in';
import { EventTracker } from './analytics/EventTracker';
import { BehavioralTracker } from './analytics/BehavioralTracker';
import { installLinkDecorator, VISITOR_URL_PARAM } from './cross-domain';
import { TRACKING_PARAM } from './tracking-params';
import { extractUtmParams, type UtmParams } from './utm';
import { resolveVisitorId, persistVisitorId } from './visitor-id';
import { resolveSessionId, startNewSession, touchSession } from './session-id';
import { watchPageViews } from './analytics/page-views';
import { withBrowserTimeZone } from './browser-timezone';
import { detectAppSurface } from './app-surface';


import { PushManager } from './push/PushManager';
import type { PushStatus, PushPermission } from './push/PushManager';

// Core
import { EventBus } from './core/EventBus';
import { TriggerEngine } from './triggers/TriggerEngine';

// v6 thin client components
import { detectAndCreateAdapter, detectPlatform } from './adapters';
import type { MicroAdapter, PageContext, LiveCart } from './adapters';
import { EMPTY_CART } from './adapters';
import { LiveSlots } from './live-slots';
import { ChunkLoader } from './chunk-loader';
import type { RenderModule } from './chunk-loader';


import type {
  HamlaSDKConfig,
  HamlaSDKState,
  TrackingEvent,
  AutoModeConfig,
  IdentifyData,
  IdentifyResult,
  SubscriptionChannel,
  SubscribeOptions,
  SubscribeResult,
} from './types/sdk.types';
import type { SendResult } from './types/tracking.types';
import type { TriggerFiredEvent } from './types/event-bus.types';
import type { NotificationType } from './types/render-engine.types';

/**
 * Default SDK configuration
 */
// NOTE: `platform` is intentionally NOT defaulted here. Hardcoding it to
// 'salla' shipped the Salla adapter + a bogus `platform: 'salla'` to
// /api/sdk/init on every site that didn't pass one explicitly (raw websites,
// which are `source/website`). The constructor derives it instead:
// config.platform (explicit) ?? detectPlatform() (from the page).
const DEFAULT_CONFIG: Required<Omit<HamlaSDKConfig, 'businessId' | 'platformId' | 'autoMode' | 'platform' | 'channel' | 'locale' | 'environment'>> & {
  autoMode: AutoModeConfig;
  channel: 'notification';
  locale: 'ar';
} = {
  apiEndpoint: 'https://app.hamla.io',
  // Same-registrable-domain hops are decorated without configuration; this is
  // only for a business whose app sits on a genuinely separate domain.
  crossDomain: [],
  debug: false,
  cacheEnabled: true,
  cacheTTL: 5 * 60 * 1000,
  autoMode: {
    enabled: false,
    fetchOnInit: true,
  },
  channel: 'notification',
  locale: 'ar',
};

/**
 * HamlaSDK v6 - Thin client with server-decided orchestration
 */
export class HamlaSDK {
  private static instance: HamlaSDK | null = null;

  private logger: Logger;
  private config: Required<HamlaSDKConfig>;
  private state: HamlaSDKState;

  // Internal components
  private fetcher: CampaignFetcher;
  private instructionExecutor: InstructionExecutor | null = null;
  private popupReferee: PopupReferee | null = null;
  /** Popups this visitor closed, remembered across visits. */
  private closeMemory = new CloseMemory();
  private webPushOptInController: WebPushOptInController | null = null;
  private tracker: EventTracker;

  // Auto-mode components
  private eventBus: EventBus | null = null;
  private triggerEngine: TriggerEngine | null = null;

  // v6 thin client components (replace PlatformAdapter, VariableResolver, CacheManager, RenderEngine)
  private adapter: MicroAdapter | null = null;
  private liveSlots: LiveSlots | null = null;
  private chunkLoader: ChunkLoader | null = null;
  /** The visitor's country from the init response — see the render config. */
  private visitorCountry: string | null = null;

  // Render queue: instructions that fire before chunk is loaded
  private renderQueue: Array<{ instruction: any; triggerKey: string }> = [];
  /** Trigger keys mid-render. The await on the render chunk leaves a window
   *  where a second fire could render the same popup twice. */
  private renderInFlight = new Set<string>();
  private reportedSuppressions = new Set<string>();

  private pushManager: PushManager | null = null;
  private behavioralTracker: BehavioralTracker | null = null;
  private autoModeStarted: boolean = false;

  // Page context and cart (collected at init, reused for events)
  private pageContext: PageContext | null = null;
  private currentCart: LiveCart = EMPTY_CART;

  // UTM attribution params (extracted from URL or referrer at init)
  private utmParams: UtmParams | null = null;

  // Removes the outbound-link visitor-id decorator. See cross-domain.ts.
  private teardownLinkDecorator: () => void = () => {};
  private teardownPageViews: () => void = () => {};

  /**
   * Private constructor - use HamlaSDK.init() instead
   */
  private constructor(config: HamlaSDKConfig) {
    this.config = {
      ...DEFAULT_CONFIG,
      ...config,
      // An explicit platform wins (Salla/Zid app installs pass it); otherwise
      // detect it from the page so a raw website is 'custom', not 'salla'.
      platform: config.platform ?? detectPlatform(),
      autoMode: {
        ...DEFAULT_CONFIG.autoMode,
        ...config.autoMode,
      },
    } as Required<HamlaSDKConfig>;

    this.logger = new Logger('[HamlaSDK]');

    this.state = {
      initialized: true,
      businessId: config.businessId || null,
      campaigns: [],
      lastFetch: null,
      // One visit across page loads, not one per page — see session-id.ts.
      sessionId: resolveSessionId(),
      impressionCount: 0,
      orchestrationSettings: null,
    };

    // Initialize internal components
    this.fetcher = new CampaignFetcher(this.config.apiEndpoint);
    this.tracker = new EventTracker({
      apiEndpoint: this.config.apiEndpoint,
      businessId: this.config.businessId,
      platformId: this.config.platformId,
      platform: this.config.platform,
      sessionId: this.state.sessionId,
      visitorId: this.getOrCreateVisitorId(),
      debug: this.config.debug,
      onActivity: () => touchSession(this.state.sessionId),
    });

    // Track off-site click if visitor came from email/SMS/push link
    this.trackOffsiteClickFromUrl();

    // Carry the visitor id on links that leave this host for our own app.
    // The cookie is supposed to cover that hop and in an in-app webview it does
    // not survive reliably — see cross-domain.ts. Installed here so it is on
    // before the first click, and torn down by destroy().
    this.teardownLinkDecorator = installLinkDecorator({
      getVisitorId: () => this.getOrCreateVisitorId(),
      extraHosts: this.config.crossDomain,
    });

    // Extract UTM parameters from URL (or detect referrer as fallback)
    this.utmParams = extractUtmParams();

    // Pass UTM params to EventTracker so every event batch includes attribution
    if (this.utmParams) {
      this.tracker.updateUtmParams(this.utmParams);
    }

    // Initialize auto-mode if enabled
    if (this.config.autoMode?.enabled) {
      this.initializeAutoMode();
    }

    this.logger.info('SDK initialized', {
      businessId: config.businessId,
      debug: this.config.debug,
      autoMode: this.config.autoMode?.enabled ?? false,
    });

    // Auto-fetch instructions if enabled
    if (this.config.autoMode?.fetchOnInit) {
      this.fetchInstructions().then((hasInstructions) => {
        this.logger.info('Auto-fetched instructions on init', { hasInstructions });
        if (this.config.autoMode?.enabled && hasInstructions) {
          this.startAutoMode();
        }
      }).catch((error) => {
        this.logger.error('Failed to auto-fetch instructions', error);
      });
    }
  }

  // ==========================================
  // Auto-Mode Initialization (v6)
  // ==========================================

  /**
   * Initialize auto-mode components using v6 thin client architecture.
   *
   * 1. Detect MicroAdapter (Salla/Zid)
   * 2. Collect page context + cart
   * 3. Create EventBus + TriggerEngine
   * 4. Create LiveSlots + ChunkLoader
   * 5. Wire adapter events (cart changes, user login)
   */
  private initializeAutoMode(): void {
    this.logger.info('Initializing auto-mode (v6 thin client)');

    // Create EventBus (central communication)
    this.eventBus = new EventBus({ debug: this.config.debug });

    // 1. Detect and create MicroAdapter
    try {
      this.adapter = detectAndCreateAdapter();
      const detection = this.adapter.detect();
      this.logger.info('MicroAdapter created', { platform: detection.platform });
    } catch (e) {
      this.logger.warn('Failed to create MicroAdapter, continuing without it', e);
    }

    // 2. Collect page context
    if (this.adapter) {
      this.pageContext = this.adapter.getPageContext();
      this.logger.info('Page context collected', { pageType: this.pageContext.pageType });
    }

    // 3. Get initial cart (async, best effort)
    if (this.adapter) {
      this.adapter.getCart().then((cart) => {
        this.currentCart = cart;
        // Update TriggerEngine with initial cart state
        if (this.triggerEngine) {
          this.triggerEngine.setInitialCartState(cart.itemCount > 0);
        }
        this.logger.info('Initial cart loaded', { itemCount: cart.itemCount });
      }).catch(() => {
        this.currentCart = EMPTY_CART;
        this.logger.info('Cart not available, using empty');
      });
    }

    // 3b. Detect order confirmation page → send complete_checkout for attribution
    if (this.pageContext?.pageType === 'order_confirmation') {
      this.logger.info('Order confirmation page detected', { orderCode: this.pageContext.orderCode });
      this.tracker.track({
        type: 'complete_checkout' as any,
        timestamp: Date.now(),
        metadata: {
          orderId: this.pageContext.orderCode || 'unknown',
          source: 'page_detection',
          pageUrl: this.pageContext.url,
        },
      });
    }

    // 4. Create TriggerEngine
    this.triggerEngine = new TriggerEngine(this.eventBus, {
      debug: this.config.debug,
      ...this.config.autoMode?.triggers,
    });

    // Set page context on TriggerEngine
    if (this.pageContext) {
      this.triggerEngine.setPageContext(this.pageContext);
    }

    // 5. Create LiveSlots (currency/locale from config, will be updated from server response)
    this.liveSlots = new LiveSlots({
      currency: 'SAR',
      locale: this.config.locale || 'ar',
    });

    // 6. Create ChunkLoader and prefetch render chunk
    this.chunkLoader = new ChunkLoader();
    const renderChunkUrl = this.config.autoMode?.renderChunkUrl
      || this.resolveRenderChunkUrl();
    this.chunkLoader.prefetch(renderChunkUrl);

    // 7. Wire adapter events
    if (this.adapter) {
      // Cart changes → update LiveSlots, TriggerEngine, and track
      this.adapter.onCartChange((newCart) => {
        this.currentCart = newCart;
        if (this.liveSlots && this.instructionExecutor) {
          const instructions = this.instructionExecutor.getAllInstructions();
          this.liveSlots.resolve(instructions, newCart);
        }
        this.triggerEngine?.updateCartState(newCart.itemCount > 0);
        this.tracker.track({
          type: 'cart_update' as any,
          timestamp: Date.now(),
          metadata: { itemCount: newCart.itemCount, total: newCart.total },
        });
      });

      // Cart item removed → emit event for remove_from_cart trigger
      this.adapter.onCartChange((cart) => {
        // TriggerEngine handles cart_abandoned via updateCartState
        if (this.eventBus && cart.itemCount === 0) {
          this.eventBus.emit('cart:item_removed', {
            timestamp: Date.now(),
            itemCount: 0,
            cartValue: 0,
          });
        }
      });

      // Checkout intent
      this.adapter.onCheckoutIntent(() => {
        this.logger.info('Checkout intent detected');
      });

      // User login → auto-identify
      this.adapter.onUserLogin(async (user) => {
        this.logger.info('User logged in via adapter', { userId: user.id });
        await this.identify({
          platformCustomerId: user.id,
          email: user.email,
        });
      });
    }

    // 8. Subscribe to trigger:fired events
    this.eventBus.on('trigger:fired', (event) => {
      this.handleTriggerFired(event);
    });

    // 9. Subscribe to notification events for tracking
    this.eventBus.on('notification:shown', (event) => {
      const toolType = this.mapNotificationToToolType(event.type as NotificationType);
      this.trackImpression(
        event.notificationId,
        event.campaignId,
        toolType,
        event.actionId,
        event.nodeId
      );
    });

    this.eventBus.on('notification:dismissed', async (event) => {
      // Collect context for off-site channel scheduling
      let context: TrackingEvent['context'] | undefined;
      if (this.adapter) {
        try {
          const cart = this.currentCart;
          context = {
            cart: {
              value: cart.total,
              currency: cart.currency,
              itemCount: cart.itemCount,
            },
          };
        } catch (e) {
          this.logger.warn('Failed to get context for dismiss event', e);
        }
      }

      const toolType = this.mapNotificationToToolType(event.type as NotificationType);
      if (toolType === 'onsite/popup' && event.dismissReason === 'user_close' && event.campaignId) {
        this.closeMemory.recordClose(event.campaignId);
      }
      this.trackDismiss(
        event.notificationId,
        event.campaignId,
        toolType,
        event.actionId,
        event.nodeId,
        context,
        { dismissReason: event.dismissReason, durationShown: event.durationShown }
      );
    });

    // A message that was due but held back is reported once per campaign,
    // reason and page, so the business can see what its rules cost it.
    this.eventBus.on('campaign:suppressed', (event) => {
      if (!event.campaignId) return;
      const key = `${event.campaignId}|${event.reason}|${window.location.pathname}`;
      if (this.reportedSuppressions.has(key)) return;
      this.reportedSuppressions.add(key);
      this.trackSuppressed(event.campaignId, event.reason, event.nodeId, event.toolType);
    });

    this.eventBus.on('cta:clicked', (event) => {
      this.trackClick(
        event.notificationId,
        event.campaignId,
        'onsite/popup',
        event.actionId,
        event.nodeId,
        event.context,
        // WHICH element was pressed. Every renderer has always emitted this and
        // this handler dropped it, so a popup's clicks arrived as one
        // undifferentiated number: a coupon copy and a signup tap were the same
        // event, and a design with two CTAs could not say which one worked.
        //
        // It rides in `metadata`, not `context`: `context` is the typed
        // identity/cart shape the ingest route validates, and an unknown key
        // there is dropped. `metadata` is a free record that the same route
        // spreads into the signal's `extra`, which is what makes this readable
        // later.
        //
        // Values are the vocabulary each renderer already emits and are NOT
        // normalised here — canvas documents send open_url/copy_coupon, the
        // section renderers send redirect/copyCoupon. Renaming either would
        // rewrite what live campaigns mean by the same word.
        {
          ctaType: event.ctaType,
          ...(event.ctaLabel ? { ctaLabel: event.ctaLabel } : {}),
          ...(event.ctaUrl ? { ctaUrl: event.ctaUrl } : {}),
        }
      );
    });

    this.eventBus.on('notification:converted', (event) => {
      this.trackConversion(
        event.notificationId,
        event.campaignId,
        'onsite/popup',
        event.actionId,
        { conversionType: event.conversionType },
        event.nodeId,
        event.context
      );
    });

    // 10. Initialize behavioral tracker
    this.behavioralTracker = new BehavioralTracker(this.tracker, {
      debug: this.config.debug,
    });
    this.behavioralTracker.onConversion(() => {
      this.logger.info('Purchase detected — server handles conversion suppression on next init');
    });

    // Every page the visitor sees, including in-page navigations — see
    // analytics/page-views.ts. The adapter is asked again on each page, since
    // the type it read at load belongs to the first page only.
    this.teardownPageViews = watchPageViews((href) => {
      const pageType = this.adapter?.getPageContext().pageType ?? this.pageContext?.pageType ?? 'other';
      this.behavioralTracker?.trackPageView(pageType, href);
    });

    this.logger.info('Auto-mode components initialized (v6)');
  }

  // ==========================================
  // Trigger Handling (v6)
  // ==========================================

  /**
   * Handle trigger:fired event — render via ChunkLoader
   */
  private async handleTriggerFired(event: TriggerFiredEvent): Promise<void> {
    this.logger.info('Trigger fired', { triggerType: event.triggerType, campaignId: event.campaignId });

    if (!this.instructionExecutor) {
      this.logger.info('No instructions loaded — skipping');
      return;
    }

    // Look up instruction by campaignId
    const match = this.instructionExecutor.findByCampaignId(event.campaignId);
    if (!match) {
      const held = this.instructionExecutor.heldBySessionCap(event.campaignId);
      if (held) {
        this.eventBus?.emit('campaign:suppressed', {
          timestamp: Date.now(),
          reason: 'session_cap',
          decisionId: `${event.campaignId}:session_cap`,
          campaignId: event.campaignId,
          nodeId: (held as any).nodeId,
          toolType: held.render?.type,
        });
      }
      this.logger.info('No instruction for campaign', { campaignId: event.campaignId });
      return;
    }

    const { triggerKey, instruction } = match;

    // Check if server flagged this instruction for skip (e.g., unresolved variables)
    if ((instruction as any).skip) {
      const skip = (instruction as any).skip;
      this.logger.info('Instruction skipped by server', {
        campaignId: instruction.campaignId,
        reason: skip.reason,
        variables: skip.variables,
      });
      this.instructionExecutor.markShown(triggerKey);
      // Report skip event so server can advance the flow
      this.trackSkip(
        `${instruction.campaignId}-${(instruction as any).nodeId}`,
        instruction.campaignId,
        (instruction as any).nodeId,
        skip.reason,
      );
      return;
    }

    // NOT marked shown here. The impression is recorded once `render.show()`
    // has actually run — see renderViaChunk. Marking it up front meant a
    // popup that rendered nothing still spent its slot, so the shopper saw
    // nothing AND the next attempt was suppressed by a message that had never
    // appeared. `renderInFlight` covers the gap the await below opens.
    if (this.renderInFlight.has(triggerKey)) return;

    // A popup this visitor closed rests for them, across visits — see CloseMemory.
    const resting =
      instruction.render?.type === 'onsite/popup' ? this.closeMemory.resting(instruction.campaignId) : null;
    if (resting) {
      this.logger.info('Popup resting after a close', { campaignId: instruction.campaignId, reason: resting });
      this.eventBus?.emit('campaign:suppressed', {
        timestamp: Date.now(),
        reason: resting,
        decisionId: `${instruction.campaignId}:${triggerKey}`,
        campaignId: instruction.campaignId,
        nodeId: (instruction as any).nodeId,
        toolType: instruction.render?.type,
      });
      return;
    }

    // Asked here, at the moment of showing, because only now does the SDK
    // know the page the visitor is on — see PopupReferee.
    const decision = this.popupReferee?.claim({
      isPopup: instruction.render?.type === 'onsite/popup',
      repeat: this.instructionExecutor.hasShown(triggerKey),
      allowProtected: instruction.pagePolicy?.allowProtected,
    });
    if (decision && !decision.allow) {
      this.logger.info('Message held back', { campaignId: instruction.campaignId, reason: decision.reason });
      this.eventBus?.emit('campaign:suppressed', {
        timestamp: Date.now(),
        reason: decision.reason,
        decisionId: `${instruction.campaignId}:${triggerKey}`,
        campaignId: instruction.campaignId,
        nodeId: (instruction as any).nodeId,
        toolType: instruction.render?.type,
      });
      return;
    }

    this.renderInFlight.add(triggerKey);

    // Resolve live slots if needed
    if (this.liveSlots) {
      this.liveSlots.resolve({ [triggerKey]: instruction }, this.currentCart);
    }

    // Get render module from ChunkLoader
    const render = await this.chunkLoader?.getRender();
    if (!render) {
      // Chunk not loaded yet — queue the instruction. The flush path marks the
      // impression, so the guard is released there rather than here.
      this.renderQueue.push({ instruction, triggerKey });
      this.logger.info('Render chunk not ready, queued instruction', { triggerKey });

      // Wait for chunk and flush queue
      this.waitForChunkAndFlush();
      return;
    }

    // Render via chunk
    this.renderViaChunk(render, instruction, triggerKey);
  }

  /**
   * Render an instruction via the loaded render chunk
   */
  private renderViaChunk(render: RenderModule, instruction: any, triggerKey: string): void {
    try {
      // Bridge MicroAdapter → RenderAdapter for the render chunk
      const adapter = this.adapter;
      const renderAdapter = adapter ? {
        addToCart: adapter.addToCart?.bind(adapter),
        applyCoupon: adapter.applyCoupon?.bind(adapter),
        refreshCart: () => window.location.reload(),
        getCartUrl: () => '/cart',
        getPageInfo: () => {
          const pc = this.pageContext;
          return { type: (pc?.pageType || 'other') as any, productId: pc?.productId };
        },
        requestPushPermission: async () => {
          await this.initPush();
          await this.subscribeToPush();
        },
      } : undefined;

      render.show(instruction, {
        eventBus: this.eventBus,
        renderAdapter,
        businessId: this.config.businessId || this.config.platformId,
        platform: this.config.platform,
        apiEndpoint: this.config.apiEndpoint,
        debug: this.config.debug,
        renderConfig: {
          formSubmitEndpoint: `${this.config.apiEndpoint}/api/sdk/subscribe`,
          businessId: this.config.businessId || this.config.platformId,
          platform: this.config.platform,
          visitorCountry: this.visitorCountry,
        },
        initPush: () => this.initPush(),
        subscribeToPush: () => this.subscribeToPush(),
      });

      // Rendered — now it counts against the session.
      this.instructionExecutor?.markShown(triggerKey);
    } catch (e) {
      // Deliberately NOT marked shown: nothing reached the shopper, so the
      // slot stays available and the next page can try again.
      if (instruction?.render?.type === 'onsite/popup' && !this.instructionExecutor?.hasShown(triggerKey)) {
        this.popupReferee?.release();
      }
      this.logger.error('Failed to render instruction', e);
    } finally {
      this.renderInFlight.delete(triggerKey);
    }
  }

  /**
   * Wait for chunk loader to be ready, then flush queued instructions
   */
  private async waitForChunkAndFlush(): Promise<void> {
    if (!this.chunkLoader) return;
    const render = await this.chunkLoader.getRender();
    if (!render || this.renderQueue.length === 0) return;

    this.logger.info('Flushing render queue', { count: this.renderQueue.length });
    const queue = [...this.renderQueue];
    this.renderQueue = [];

    for (const { instruction, triggerKey } of queue) {
      this.renderViaChunk(render, instruction, triggerKey);
    }
  }

  /**
   * Derive the render chunk URL from the SDK script's own location.
   * If hamla.js was loaded from https://app.hamla.io/sdk/hamla.js,
   * the render chunk is at https://app.hamla.io/sdk/sdk-render.js.
   * Falls back to apiEndpoint/sdk-render.js if script src is unavailable.
   */
  private resolveRenderChunkUrl(): string {
    if (typeof document !== 'undefined') {
      // Find the script tag that loaded us by matching known filenames
      const scripts = Array.from(document.querySelectorAll('script[src]'));
      for (let i = 0; i < scripts.length; i++) {
        const src = (scripts[i] as HTMLScriptElement).src;
        if (src.includes('hamla') && (src.endsWith('.js') || src.endsWith('.mjs'))) {
          const base = src.substring(0, src.lastIndexOf('/') + 1);
          return `${base}sdk-render.js`;
        }
      }
    }
    return `${this.config.apiEndpoint}/sdk-render.js`;
  }

  // ==========================================
  // Static Methods
  // ==========================================

  /**
   * Initialize the SDK (singleton pattern)
   */
  public static init(config: HamlaSDKConfig): HamlaSDK {
    if (HamlaSDK.instance) {
      const logger = new Logger('[HamlaSDK]');
      logger.info('SDK already initialized, returning existing instance');
      return HamlaSDK.instance;
    }

    if (!config.businessId && !config.platformId) {
      throw new Error('HamlaSDK: Either businessId or platformId is required');
    }

    if (config.platformId && !config.platform) {
      throw new Error('HamlaSDK: platform is required when using platformId');
    }

    HamlaSDK.instance = new HamlaSDK(config);
    return HamlaSDK.instance;
  }

  /**
   * Get the current SDK instance
   */
  public static getInstance(): HamlaSDK | null {
    return HamlaSDK.instance;
  }

  /**
   * Check if SDK is initialized
   */
  public static isInitialized(): boolean {
    return HamlaSDK.instance !== null;
  }

  // ==========================================
  // Server Instructions (v6)
  // ==========================================

  /**
   * Fetch render instructions from /api/sdk/init
   *
   * v6: sends pageContext + cart to server (collected by MicroAdapter)
   */
  public async fetchInstructions(): Promise<boolean> {
    this.logger.info('fetchInstructions called');

    const result = await this.fetcher.fetchInstructions({
      businessId: this.config.businessId,
      platformId: this.config.platformId as string | number | undefined,
      platform: this.adapter?.detect().platform || this.config.platform,
      visitorId: this.getOrCreateVisitorId(),
      sessionId: this.state.sessionId,
      pageContext: this.pageContext || undefined,
      cart: this.currentCart || undefined,
      isFirstVisit: typeof localStorage !== 'undefined' ? !localStorage.getItem('hamla_visited') : undefined,
      referrer: typeof document !== 'undefined' ? document.referrer : undefined,
      // Browser or installed app. The page can check the shell's globals
      // directly; the server can only infer it from a User-Agent, and does so
      // conservatively, so this is the signal it prefers when present.
      appSurface: detectAppSurface(),
      // Declared by the business on the snippet. The server cannot derive it —
      // a marketing page and a logged-in page share host, Origin and
      // User-Agent — so if the page does not say, it is not said.
      environment: this.config.environment,
      utmParams: this.utmParams,
    });

    if (!result.success) {
      this.logger.error('Failed to fetch instructions', result.error);
      return false;
    }

    // Create InstructionExecutor from server response
    this.instructionExecutor = new InstructionExecutor(result.data);
    this.popupReferee = new PopupReferee({
      popupsPerPageView: result.data.sessionLimits?.popupsPerPageView,
      popupCooldownMs: result.data.sessionLimits?.popupCooldownMs,
    });

    // Schedule the global, merchant-controlled Web Push opt-in banner.
    // Independent from campaigns: only renders after init + a visitor click.
    if (!this.webPushOptInController) {
      this.webPushOptInController = createWebPushOptInController({
        storagePrefix: this.config.businessId || String(this.config.platformId || 'unknown'),
        subscribeToPush: () => this.subscribeToPush(),
      });
    }
    this.webPushOptInController.schedule(result.data.webPushOptIn, this.pageContext);

    // Where the visitor is, decided at the edge. Kept for the render chunk,
    // which is loaded lazily and cannot ask: capture forms start their phone
    // field on this country instead of guessing it from a timezone, and
    // campaign targeting already trusts the same fact.
    this.visitorCountry = result.data.visitor?.country ?? null;

    // Update businessId if resolved
    const storeInfo = result.data.storeInfo;
    if (storeInfo?.id && !this.state.businessId) {
      this.state.businessId = storeInfo.id;
      this.config.businessId = storeInfo.id;
      this.tracker.updateBusinessId(storeInfo.id);
    }

    // Register triggers with TriggerEngine
    const triggerKeys = this.instructionExecutor.getTriggerKeys();
    if (this.triggerEngine) {
      for (const key of triggerKeys) {
        const instr = this.instructionExecutor.getInstruction(key);
        if (!instr) continue;
        this.triggerEngine.registerTrigger(
          instr.campaignId,
          instr.trigger.type,
          instr.trigger.config as Record<string, unknown> | undefined,
        );
      }
      this.logger.info('Instructions registered with TriggerEngine', { triggerKeys });
    } else {
      this.logger.info('Instructions loaded (no TriggerEngine)', { triggerKeys });
    }

    return triggerKeys.length > 0;
  }

  /**
   * Start auto-mode after instructions are loaded
   */
  public startAutoMode(): void {
    if (!this.config.autoMode?.enabled) {
      this.logger.info('Auto-mode not enabled');
      return;
    }

    if (!this.triggerEngine) {
      this.logger.info('TriggerEngine not initialized');
      return;
    }

    if (this.autoModeStarted) {
      this.logger.warn('Auto-mode already started, ignoring duplicate call');
      return;
    }

    for (const campaign of this.state.campaigns) {
      this.triggerEngine.registerCampaign(campaign);
    }

    this.autoModeStarted = true;
    this.logger.info('Auto-mode started');
  }

  // ==========================================
  // Tracking Methods
  // ==========================================

  public trackImpression(
    decisionId: string,
    campaignId: string,
    toolType: string,
    actionId?: string,
    nodeId?: string
  ): void {
    this.state.impressionCount++;
    this.tracker.track({
      type: 'impression',
      decisionId,
      campaignId,
      actionId,
      toolType,
      nodeId,
      timestamp: Date.now(),
    });
  }

  public trackClick(
    decisionId: string,
    campaignId: string,
    toolType: string,
    actionId?: string,
    nodeId?: string,
    context?: TrackingEvent['context'],
    /**
     * Detail about the press itself — which CTA, its label, its URL.
     *
     * Last, rather than fifth the way `trackConversion` takes its metadata:
     * this method is public and a page may already be calling it
     * positionally, so the existing arguments cannot move.
     */
    metadata?: Record<string, unknown>
  ): void {
    this.tracker.track({
      type: 'click',
      decisionId,
      campaignId,
      actionId,
      toolType,
      nodeId,
      timestamp: Date.now(),
      context,
      metadata,
    });
  }

  public trackDismiss(
    decisionId: string,
    campaignId: string,
    toolType: string,
    actionId?: string,
    nodeId?: string,
    context?: TrackingEvent['context'],
    metadata?: { dismissReason?: string; durationShown?: number }
  ): void {
    this.tracker.track({
      type: 'dismiss',
      decisionId,
      campaignId,
      actionId,
      toolType,
      nodeId,
      timestamp: Date.now(),
      context,
      metadata,
    });
  }

  /** A message that was due but held back, with the reason. */
  public trackSuppressed(
    campaignId: string,
    reason: string,
    nodeId?: string,
    toolType = 'onsite/popup'
  ): void {
    this.tracker.track({
      type: 'suppressed',
      decisionId: `${campaignId}:suppressed`,
      campaignId,
      nodeId,
      toolType,
      timestamp: Date.now(),
      metadata: { reason },
    });
  }

  public trackSkip(
    decisionId: string,
    campaignId: string,
    nodeId?: string,
    reason?: string,
  ): void {
    this.tracker.track({
      type: 'skip',
      decisionId,
      campaignId,
      nodeId,
      toolType: 'onsite/notification',
      timestamp: Date.now(),
      metadata: reason ? { skipReason: reason } : undefined,
    });
  }

  public trackConversion(
    decisionId: string,
    campaignId: string,
    toolType: string,
    actionId?: string,
    metadata?: Record<string, unknown>,
    nodeId?: string,
    context?: TrackingEvent['context']
  ): void {
    this.tracker.track({
      type: 'conversion',
      decisionId,
      campaignId,
      actionId,
      toolType,
      nodeId,
      timestamp: Date.now(),
      metadata,
      context,
    });
  }

  public async flush(): Promise<SendResult> {
    return this.tracker.flush();
  }

  /**
   * Track a generic event. The primary SDK primitive for custom merchant events.
   *
   * Use this when none of the specialized helpers (trackAddToCart, etc.) fit.
   * The event name is passed verbatim to the server and can be used as a
   * campaign trigger or for analytics.
   *
   * @example
   * hamla.track('appointment_booked', { date: '2026-04-15', price: 100 });
   * hamla.track('download_ebook', { asset: 'guide.pdf' });
   * hamla.track('add_to_cart', { productId: 'abc', price: 50 });
   */
  public async track(eventName: string, properties: Record<string, unknown> = {}): Promise<void> {
    this.tracker.trackCustomEvent(eventName, properties);
  }

  // ==========================================
  // State Management
  // ==========================================

  public getState(): HamlaSDKState {
    return { ...this.state };
  }

  public setSessionId(sessionId: string): void {
    this.state.sessionId = sessionId;
    this.state.impressionCount = 0;
    this.tracker.updateSessionId(sessionId);
  }

  public clearCache(): void {
    this.state.campaigns = [];
    this.state.lastFetch = null;
    this.logger.info('Cache cleared');
  }

  public async destroy(): Promise<void> {
    this.logger.info('Destroying SDK instance');

    this.teardownLinkDecorator();
    this.teardownPageViews();

    await this.tracker.destroy();

    if (this.triggerEngine) {
      this.triggerEngine.destroy();
      this.triggerEngine = null;
    }
    if (this.eventBus) {
      this.eventBus.clear();
      this.eventBus = null;
    }
    if (this.adapter) {
      this.adapter.destroy();
      this.adapter = null;
    }
    if (this.behavioralTracker) {
      this.behavioralTracker.destroy();
      this.behavioralTracker = null;
    }

    this.liveSlots = null;
    this.chunkLoader = null;
    this.renderQueue = [];
    this.pageContext = null;
    this.currentCart = EMPTY_CART;

    this.webPushOptInController?.destroy();
    this.webPushOptInController = null;

    this.state = {
      initialized: false,
      businessId: null,
      campaigns: [],
      lastFetch: null,
      sessionId: '',
      impressionCount: 0,
      orchestrationSettings: null,
    };

    HamlaSDK.instance = null;
  }

  // ==========================================
  // Public Accessors
  // ==========================================

  public getEventBus(): EventBus | null {
    return this.eventBus;
  }

  public getTriggerEngine(): TriggerEngine | null {
    return this.triggerEngine;
  }

  public getAdapter(): MicroAdapter | null {
    return this.adapter;
  }

  // ==========================================
  // Cart
  // ==========================================

  public updateCart(hasItems: boolean, cartValue?: number): void {
    if (this.triggerEngine) {
      this.triggerEngine.updateCartState(hasItems, cartValue);
    }
  }

  // ==========================================
  // Push Notifications
  // ==========================================

  public async initPush(): Promise<PushStatus> {
    if (this.pushManager) {
      return this.pushManager.getStatus();
    }

    let pushFrameUrl = this.config.autoMode?.pushFrameUrl || 'https://push.hamla.io';
    if (!this.config.autoMode?.pushFrameUrl) {
      if (this.config.apiEndpoint.includes('ngrok')) {
        pushFrameUrl = 'https://hamla-push.in.ngrok.io';
      }
    }

    this.pushManager = new PushManager(
      {
        visitorId: this.getOrCreateVisitorId(),
        businessId: this.config.businessId,
        pushFrameUrl,
        debug: this.config.debug,
      },
      this.eventBus
    );

    return this.pushManager.init();
  }

  public async subscribeToPush(): Promise<PushStatus> {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
      return {
        supported: false,
        permission: 'default',
        subscribed: false,
        error: 'Push not supported in this browser',
      };
    }

    return this.subscribeDirectly();
  }

  private async subscribeDirectly(): Promise<PushStatus> {
    const VAPID_PUBLIC_KEY = 'BHIj_dVKZhnxMUsntJpzjMnlzRTvNmgu8F2Mg1nGQaasJtPYCD3u7uHUNDPe_B-zkiVil55NyGW17QaVYqUCZLE';

    try {
      let registration: ServiceWorkerRegistration | undefined;

      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const reg of registrations) {
          if (reg.active) {
            registration = reg;
            break;
          }
        }

        if (!registration) {
          try {
            registration = await navigator.serviceWorker.register('/hamla-sw.js');
          } catch {
            return this.subscribeViaPopup();
          }
        }
      } catch {
        return this.subscribeViaPopup();
      }

      if (!registration) {
        return this.subscribeViaPopup();
      }

      await navigator.serviceWorker.ready;

      let subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        try {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: this.urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
          });
        } catch (subscribeError: any) {
          if (subscribeError.name === 'NotAllowedError') {
            return { supported: true, permission: 'denied', subscribed: false, error: 'Permission denied by user' };
          }
          return {
            supported: true,
            permission: Notification.permission as PushPermission,
            subscribed: false,
            error: `${subscribeError.name}: ${subscribeError.message}`,
          };
        }
      }

      // Send subscription to server
      const apiUrl = this.config.apiEndpoint.replace(/\/$/, '');
      try {
        const detection = this.adapter?.detect();
        await fetch(`${apiUrl}/sdk/subscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            visitorId: this.getOrCreateVisitorId(),
            businessId: this.config.businessId,
            platformId: detection?.platform,
            platform: detection?.platform,
            channel: 'push',
            value: 'push-subscription',
            subscription: subscription.toJSON(),
          }),
        });
      } catch {
        // Still return success — browser subscription is active
      }

      this.eventBus?.emit('push:subscribed' as any, {
        timestamp: Date.now(),
        visitorId: this.getOrCreateVisitorId(),
      });

      return { supported: true, permission: 'granted', subscribed: true };

    } catch (error: any) {
      return {
        supported: true,
        permission: Notification.permission as PushPermission,
        subscribed: false,
        error: error.message || 'Unknown error',
      };
    }
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  private subscribeViaPopup(): Promise<PushStatus> {
    return new Promise((resolve) => {
      let pushFrameUrl = this.config.autoMode?.pushFrameUrl || 'https://push.hamla.io';
      if (!this.config.autoMode?.pushFrameUrl && this.config.apiEndpoint.includes('ngrok')) {
        pushFrameUrl = 'https://hamla-push.in.ngrok.io';
      }

      const popupUrl = new URL(`${pushFrameUrl}/subscribe.html`);
      popupUrl.searchParams.set('visitorId', this.getOrCreateVisitorId());
      if (this.config.businessId) {
        popupUrl.searchParams.set('businessId', this.config.businessId);
      }
      const detection = this.adapter?.detect();
      if (detection?.platform) {
        popupUrl.searchParams.set('platform', detection.platform);
      }

      const width = 400;
      const height = 500;
      const left = Math.max(0, (window.screen.width - width) / 2);
      const top = Math.max(0, (window.screen.height - height) / 2);

      const popup = window.open(
        popupUrl.toString(),
        'hamla_push',
        `width=${width},height=${height},left=${left},top=${top},scrollbars=no,resizable=no`
      );

      if (!popup) {
        resolve({ supported: true, permission: 'default', subscribed: false, error: 'Popup blocked' });
        return;
      }

      const timeout = setTimeout(() => {
        cleanup();
        resolve({ supported: true, permission: 'default', subscribed: false, error: 'Subscription timeout' });
      }, 120000);

      const handleMessage = (event: MessageEvent) => {
        if (!event.origin.includes('hamla') && !event.origin.includes('ngrok')) return;
        const data = event.data;
        if (data?.type === 'HAMLA_PUSH_POPUP_RESULT') {
          cleanup();
          if (data.success) {
            this.eventBus?.emit('push:subscribed' as any, {
              timestamp: Date.now(),
              visitorId: this.getOrCreateVisitorId(),
            });
          }
          const isSubscribed = data.success && (data.subscribed || data.alreadySubscribed);
          resolve({
            supported: true,
            permission: data.permission || (data.success ? 'granted' : 'default'),
            subscribed: isSubscribed,
            error: data.error,
          });
        }
      };

      const pollInterval = setInterval(() => {
        if (popup.closed) {
          cleanup();
          resolve({ supported: true, permission: 'default', subscribed: false, error: 'Popup closed' });
        }
      }, 500);

      const cleanup = () => {
        clearTimeout(timeout);
        clearInterval(pollInterval);
        window.removeEventListener('message', handleMessage);
        try { popup.close(); } catch {}
      };

      window.addEventListener('message', handleMessage);
    });
  }

  public async unsubscribeFromPush(): Promise<PushStatus> {
    if (!this.pushManager) {
      return { supported: false, permission: 'default' as PushPermission, subscribed: false, error: 'Push not initialized' };
    }
    return this.pushManager.unsubscribe();
  }

  public async getPushStatus(): Promise<PushStatus> {
    if (!this.pushManager) {
      return { supported: false, permission: 'default' as PushPermission, subscribed: false };
    }
    return this.pushManager.getStatus();
  }

  public get isPushSubscribed(): boolean {
    return this.pushManager?.isSubscribed ?? false;
  }

  // ==========================================
  // Identity Resolution
  // ==========================================

  /** Call when a person logs out or hands a shared device to someone else. */
  public async resetIdentity(): Promise<void> {
    const visitorId = this.getOrCreateVisitorId();
    await this.tracker.flush();
    const response = await fetch(`${this.config.apiEndpoint.replace(/\/$/, '')}/api/sdk/reset`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ businessId: this.config.businessId, visitorId }),
    });
    if (!response.ok) throw new Error('Unable to reset browser identity');
    const nextVisitorId = `vis-${crypto.randomUUID()}`;
    persistVisitorId(nextVisitorId);
    const url = new URL(window.location.href);
    url.searchParams.delete(VISITOR_URL_PARAM);
    window.history.replaceState(window.history.state, '', url.toString());
    this.tracker.resetIdentity(nextVisitorId);
    this.setSessionId(startNewSession());
    this.clearCache();
  }

  public async identify(identity: IdentifyData): Promise<IdentifyResult | null> {
    if (!identity.platformCustomerId && !identity.email && !identity.phone) {
      this.logger.warn('identify() called without identity data');
      return null;
    }

    try {
      const visitorId = this.getOrCreateVisitorId();
      const apiUrl = this.config.apiEndpoint.replace(/\/$/, '');

      const response = await fetch(`${apiUrl}/api/sdk/identify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessId: this.config.businessId,
          platformId: this.config.platformId,
          platform: this.config.platform,
          visitorId,
          method: identity.method,
          identity: {
            platformCustomerId: identity.platformCustomerId,
            email: identity.email,
            phone: identity.phone,
          },
          // The browser knows its own zone exactly; an integrator would have
          // to guess. Hamla uses it to keep delayed campaign steps out of the
          // recipient's night — without it, a "+1 day" follow-up lands at
          // whatever hour they happened to sign up. Caller-supplied wins.
          profile: withBrowserTimeZone(identity.profile),
        }),
      });

      if (!response.ok) return null;

      const data = await response.json();
      if (!data.success || !data.data) return null;

      const result: IdentifyResult = {
        contactId: data.data.contactId,
        isNew: data.data.isNew,
        linkedVisitor: data.data.linkedVisitor,
        subscriptions: data.data.subscriptions,
      };

      if (this.eventBus) {
        this.eventBus.emit('visitor:identified', {
          timestamp: Date.now(),
          visitorId,
          contactId: result.contactId,
          isNew: result.isNew,
        });
      }

      return result;
    } catch (error) {
      this.logger.error('Failed to identify visitor', error);
      return null;
    }
  }

  // ==========================================
  // Subscription (Lead Capture)
  // ==========================================

  public async subscribe(
    channel: SubscriptionChannel,
    value: string,
    options?: SubscribeOptions
  ): Promise<SubscribeResult | null> {
    if (!['email', 'sms', 'whatsapp', 'push'].includes(channel)) return null;
    if (!value) return null;

    try {
      const visitorId = this.getOrCreateVisitorId();
      const apiUrl = this.config.apiEndpoint.replace(/\/$/, '');

      const response = await fetch(`${apiUrl}/api/sdk/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessId: this.config.businessId,
          platformId: this.config.platformId,
          platform: this.config.platform,
          visitorId,
          channel,
          value,
          campaignId: options?.campaignId,
          utmParams: this.utmParams,
        }),
      });

      if (!response.ok) return null;

      const data = await response.json();
      if (!data.success || !data.data) return null;

      const result: SubscribeResult = {
        contactId: data.data.contactId,
        isNew: data.data.isNew,
        channel: data.data.channel,
        alreadySubscribed: data.data.alreadySubscribed,
      };

      if (this.eventBus) {
        this.eventBus.emit('visitor:subscribed' as any, {
          timestamp: Date.now(),
          visitorId,
          contactId: result.contactId,
          channel,
          isNew: result.isNew,
        });
      }

      return result;
    } catch (error) {
      this.logger.error('Failed to subscribe visitor', error);
      return null;
    }
  }

  // ==========================================
  // Convenience Methods
  // ==========================================

  public getPendingEvents(): TrackingEvent[] {
    return this.tracker.getPendingEvents();
  }

  public getPendingEventCount(): number {
    return this.tracker.getPendingCount();
  }

  // ==========================================
  // Private Helpers
  // ==========================================

  /**
   * Map NotificationType to tool type string (for tracking)
   */
  private mapNotificationToToolType(notifType: NotificationType): string {
    switch (notifType) {
      case 'popup':
        return 'onsite/popup';
      case 'banner':
      case 'toast':
      case 'slide-in':
      default:
        return 'onsite/notification';
    }
  }


  private getOrCreateVisitorId(): string {
    // Stored in a cookie on the registrable domain as well as localStorage —
    // see visitor-id.ts. localStorage alone is scoped to one ORIGIN, so a
    // visitor who clicked the ad on www and signs up on app looked like two
    // different people.
    return resolveVisitorId(this.getVisitorIdFromUrl());
  }

  private getVisitorIdFromUrl(): string | null {
    try {
      return new URLSearchParams(window.location.search).get(VISITOR_URL_PARAM);
    } catch {
      return null;
    }
  }

  /**
   * Returns the UTM parameters extracted at SDK initialization.
   * Includes params from the URL or detected from document.referrer as fallback.
   */
  public getUtmParams(): UtmParams | null {
    return this.utmParams;
  }

  private trackOffsiteClickFromUrl(): void {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const campaignId = urlParams.get(TRACKING_PARAM.CAMPAIGN_ID);
      const actionId = urlParams.get(TRACKING_PARAM.CHANNEL_ID);
      const decisionId = urlParams.get(TRACKING_PARAM.DECISION_ID);
      const toolType = urlParams.get(TRACKING_PARAM.CHANNEL_TYPE) as 'channel/email' | 'channel/sms' | 'channel/whatsapp' | 'channel/push' | null;

      if (campaignId) {
        this.tracker.track({
          type: 'click',
          campaignId,
          decisionId: decisionId || `offsite-${Date.now()}`,
          actionId: actionId || undefined,
          toolType: toolType || 'channel/email',
          timestamp: Date.now(),
          metadata: { source: 'offsite', pageUrl: window.location.href },
        });

        try {
          localStorage.setItem('hamla_has_interactions', '1');
        } catch { /* ignore */ }
      }
    } catch {
      // Ignore errors
    }
  }
}
